import { ActionForm } from '@features/ksp-actions/ActionForm';
import { Muted, Notice, Stack } from '../styles';
import { choice, rdCatalogue, textField } from '../types';

export interface InitialSetupProps {
  projectId: string;
  canWrite: boolean;
  historyReadOnly?: boolean;
  busy: boolean;
  errors: string[];
  refetch: () => void;
  execute: (
    path: string,
    body: Record<string, unknown>,
    method?: 'POST' | 'PATCH' | 'DELETE',
  ) => Promise<unknown>;
}

export function InitialSetup({
  projectId,
  canWrite,
  historyReadOnly,
  busy,
  errors,
  refetch,
  execute,
}: InitialSetupProps) {
  return (
    <Stack>
      <h1>Начать планирование проекта</h1>
      <Muted>
        Настройки задают календарные границы и стороны проекта. Источники и права сохраняются
        внутри этого проекта.
      </Muted>
      {errors.map((text, index) => (
        <Notice $error key={index}>
          {text}
        </Notice>
      ))}
      {canWrite && !historyReadOnly ? (
        <ActionForm
          busy={busy}
          onClose={() => refetch()}
          spec={{
            title: 'Настройки нового проекта КСП',
            submit: 'Создать контур КСП',
            fields: [
              textField('timezone', 'Часовой пояс IANA', true, 'Europe/Moscow'),
              {
                ...textField('notification_time', 'Время ежедневного сообщения', true, '09:00'),
                type: 'time',
              },
              textField('organization_name', 'Организация-отправитель'),
              textField('customer_name', 'Заказчик'),
              {
                ...textField(
                  'data_date',
                  'Дата состояния UTC',
                  true,
                  new Date().toISOString().slice(0, 16),
                ),
                type: 'datetime-local',
              },
              choice(
                'rd_coverage',
                'Полнота рабочей документации',
                [
                  { value: 'partial_rd', label: 'Подтверждена часть РД' },
                  { value: 'complete_rd', label: 'Полная РД' },
                ],
                'partial_rd',
              ),
              textField('coverage_basis', 'Основание выбранной полноты'),
              {
                ...textField(
                  'expected_rd_codes',
                  'Ожидаемые шифры комплектов РД (по одному в строке)',
                  false,
                ),
                type: 'textarea',
                help: 'Утверждённый перечень для подсчёта полноты. Пустой перечень означает неизвестную полноту.',
              },
              textField('expected_rd_basis', 'Основание ожидаемого перечня РД', false),
            ],
            onSubmit: async (values) => {
              try {
                await execute(
                  `projects/${projectId}/settings`,
                  {
                    ...values,
                    ...rdCatalogue(values),
                    data_date: new Date(`${values.data_date}:00Z`).toISOString(),
                  },
                  'PATCH',
                );
              } catch {
                /* persistent issue */
              }
            },
          }}
        />
      ) : (
        <Notice>
          Настройки КСП ещё не созданы. Для начала работы требуется специалист с правом изменения
          проекта.
        </Notice>
      )}
    </Stack>
  );
}
